// voiceSessionLimit — how long a live voice call is allowed to run (issue #377).
//
// THE BUG THIS EXISTS FOR: the 20 min/day voice cap was enforced ONLY at
// connection time. kin-voice-session 429s a mint request once the day's total
// is already at 1200s, but after the ephemeral token is handed back the client
// talks straight to api.openai.com over WebRTC and no server code is in the
// loop again. A user at 1140/1200s passed the gate (correctly — 60s left) and
// then ran the full per-call 10 minutes, ending the day at 1743s. Every fresh
// session below the cap granted up to another 10 uncapped PAID minutes.
//
// THE FIX: kin-voice-session now returns remainingDailySeconds alongside the
// token, and the session timer is set to min(per-call limit, quota left)
// instead of always the per-call limit. This module is that min(), split out
// as a pure function so it's unit-testable without touching the WebRTC hook
// (a locked zone — see CLAUDE.md).
//
// TWO INDEPENDENT LIMITS, do not merge them:
//   - per-call: MAX_SESSION_MS, 10 minutes, unchanged, applies to every call.
//   - daily:    the remaining slice of the 20 min/day cap.
// Whichever is smaller wins. The per-call limit keeps working on its own for
// anyone with plenty of quota left.

export type VoiceSessionLimitReason = "per-call" | "daily-cap";

export interface VoiceSessionLimit {
  /** Milliseconds from NOW until the call must end. Never negative. */
  limitMs: number;
  /** Which limit won — drives which message she sees when it fires. */
  reason: VoiceSessionLimitReason;
}

export interface ComputeVoiceSessionLimitArgs {
  /** The per-call ceiling (useRealtimeVoice's MAX_SESSION_MS). */
  maxSessionMs: number;
  /**
   * Seconds of daily quota left as of the mint, straight from the server.
   * `null` means no daily limit applies (admin). `undefined` / a non-number
   * means the response didn't carry the field — see FAIL-OPEN below.
   */
  remainingDailySeconds: number | null | undefined;
  /**
   * How long ago the session was minted. The server measures the remaining
   * quota from the mint instant, and duration_seconds is anchored to that same
   * instant, so the time spent on the token fetch + WebRTC handshake has to
   * come off the daily budget or the day's total quietly overshoots by it.
   * The per-call limit is NOT reduced by this — it starts when the call goes
   * live, exactly as it always has.
   */
  elapsedSinceMintMs: number;
}

export function computeVoiceSessionLimit({
  maxSessionMs,
  remainingDailySeconds,
  elapsedSinceMintMs,
}: ComputeVoiceSessionLimitArgs): VoiceSessionLimit {
  const perCallMs = Math.max(0, maxSessionMs);

  // FAIL-OPEN on a missing/garbage value. The Edge Function and the frontend
  // deploy on separate pipelines (PR-merge auto-deploy vs Vercel), so there is
  // always a window where one is newer than the other. An old server that
  // doesn't send the field must not accidentally cut every call to zero — fall
  // back to exactly today's behavior, the per-call limit alone.
  if (
    remainingDailySeconds === null ||
    remainingDailySeconds === undefined ||
    typeof remainingDailySeconds !== "number" ||
    !Number.isFinite(remainingDailySeconds)
  ) {
    return { limitMs: perCallMs, reason: "per-call" };
  }

  const elapsed = Number.isFinite(elapsedSinceMintMs)
    ? Math.max(0, elapsedSinceMintMs)
    : 0;
  const dailyMs = Math.max(0, remainingDailySeconds * 1000 - elapsed);

  // Ties go to "per-call": with a full 10+ minutes of quota left this is an
  // ordinary call and she should get the ordinary 10-minute message.
  if (dailyMs >= perCallMs) {
    return { limitMs: perCallMs, reason: "per-call" };
  }
  return { limitMs: dailyMs, reason: "daily-cap" };
}

// ── The 30-second heads-up ───────────────────────────────────────────────────
//
// A daily-cap cutoff is the surprising one: she's mid-conversation and voice
// won't come back until tomorrow. Ending a call is fine; having one cut out on
// you is not. So Kin says so first, and the call ENDS rather than BREAKS.
//
// Deliberately daily-cap only. The per-call 10-minute limit has never had a
// 9:30 warning and that was an intentional call for this build — a mom who
// starts a call has 10 minutes roughly in mind, and a warning on every single
// call is noise. Hitting the day's wall isn't something she's tracking.

/** How long before the cutoff Kin gives the heads-up. */
export const DAILY_CAP_WARNING_LEAD_MS = 30_000;

/**
 * Don't bother warning if it leaves less than this much talking afterwards.
 * With 32 seconds left, "about thirty seconds" lands almost on top of the
 * goodbye — two interruptions back to back, worse than one clean ending.
 */
export const MIN_TALK_AFTER_WARNING_MS = 5_000;

/**
 * When to fire the heads-up, or null for "don't".
 *
 * Returns a delay from now, so the caller can hand it straight to setTimeout.
 * Null covers three cases that all mean the same thing to her: it isn't a
 * daily-cap call, or the call is too short for a warning to be anything but
 * noise.
 */
export function computeDailyCapWarningDelayMs(
  limit: VoiceSessionLimit,
  cutoffInMs: number,
): number | null {
  if (limit.reason !== "daily-cap") return null;
  const delay = cutoffInMs - DAILY_CAP_WARNING_LEAD_MS;
  if (delay < MIN_TALK_AFTER_WARNING_MS) return null;
  return delay;
}
